import { Box, Grid, TextField } from "@mui/material";
import * as React from "react";
import { ApiSuccess } from "ui/Scripts/source/models/common/apiSuccess";
import { ConfirmationDialogResult, TaskStateEditabilityType } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../../../framework/appUtils";
import * as store from "../../../../../framework/customStore";
import { SaveDataObj } from "../../../../../framework/saveData";
import * as taskService from "../../../../../models/services/taskService";
import * as storeActions from "../../../../../models/store/storeActions";
import { Dialog } from "../../../../framework/Dialog/Dialog";
import { DialogItemsNav } from "../../../../framework/Dialog/DialogItemsNav";
import { FormField } from "../../../../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/components/framework/FormField";
import { GridRow } from "../../../../framework/Grid/GridRow";
import { ITaskStateEditItem } from "source/models/taskState/taskStateEditItem";
import { FormCheckBox } from "source/components/framework/FormCheckBox";
import { ColorPicker } from "source/components/framework/ColorPicker/ColorPicker";
import { DialogButtons } from "source/components/framework/Dialog/DialogButtons";
import { ITaskStateListItem, TaskStateListItem } from "source/models/taskState/taskStateListItem";
import { Base } from "ui/Scripts/source/framework/base";
import { TaskStateEdit } from "source/models/taskState/taskStateEdit";

export interface ITaskStateDialogProp {
    isReadOnly?: boolean;
    id: string;
    companyId: string;
    taskTypeId: string;
    hasPrevious: boolean;
    hasNext: boolean;
    onPrevious: () => void;
    onNext: () => void;
    onSaved: (item: ITaskStateListItem) => void;
    onClose: (refreshList: boolean) => void;
    onRemove: (ids: string[]) => Promise<ApiSuccess>;
    onRefreshList: () => Promise<void>;
    onGetTaskStateEdit?: (companyId: string, id: string) => Promise<TaskStateEdit>;
}

export interface ITaskStateDialogState {
    id: string;
    rowId: string;
    isNew: boolean;
    code: string;
    comment: string;
    color: string;
    sortOrder: string;
    editabilityType: TaskStateEditabilityType;
    activeState: number;
        
    item: ITaskStateEditItem;
}

export class TaskStateDialog extends React.Component<ITaskStateDialogProp, ITaskStateDialogState> {
    private orgStateHash: string = "";

    constructor(props: ITaskStateDialogProp) {
        super(props);
        this.state = {
            item: null,
            id: "",
            rowId: "",
            isNew: false,
            code: "",
            comment: "",
            color: "",
            sortOrder: "0",
            editabilityType: TaskStateEditabilityType.NoLimitation,
            activeState: 0,
        };
    }

    isNewTaskType = (): boolean => {
        return !!this.props.onGetTaskStateEdit;
    };

    loadData = async (id: string) => {
        const props = this.props;
        const getTaskStateEdit = props.onGetTaskStateEdit ?? taskService.getTaskStateEdit;
        const result = await AppUtils.callService(() => getTaskStateEdit(props.companyId, id), false);
        if (!result) return;
        const item = result.item;
        const state = {
            ...this.state,
            item,
            id: item.id,
            rowId: item.rowId,
            isNew: !this.isNewTaskType()
                ? item.isNew()
                : item.id === Base.emptyGuid,
            code: item.code,
            comment: item.comment,
            color: item.color,
            sortOrder: item.sortOrder.toString(10),
            editabilityType: item.editabilityType,
            activeState: item.activeState
        };
        this.setState(state);
        this.orgStateHash = this.getSaveDataFromState(this.props, state).hash;
    };

    componentDidMount(): void {
        this.loadData(this.props.id);
    }

    async componentDidUpdate(prevProps: ITaskStateDialogProp, prevState: ITaskStateDialogState): Promise<void> {
        const props = this.props;
        const state = this.state;
        if (prevProps.companyId === props.companyId && prevProps.id === props.id) return;
        const cancelResult = await AppUtils.cancel(this.getSaveDataFromState(props, state).hash, this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            if (!await this.saveToDb()) return;
            props.onRefreshList();
        }
        this.loadData(props.id);
    }

    handleChange = (event) => {
        const target = event.target;
        const value: string = target.value;
        const name: string = target.name;
        if (name === "code") {
            this.setState({ code: value });
        } else if (name === "comment") {
            this.setState({ comment: value });
        } else if (name === "sortOrder") {
            this.setState({ sortOrder: value });
        }
    };

    getSaveDataFromState = (props: ITaskStateDialogProp, state: ITaskStateDialogState): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("companyId", props.companyId);
        result.append("taskTypeId", props.taskTypeId);
        result.append("id", state.id);
        result.append("rowId", state.rowId);
        result.append("code", state.code);
        result.append("comment", state.comment);
        result.append("color", state.color);
        result.append("sortOrder", state.sortOrder.toInteger());
        result.append("editabilityType", state.editabilityType);
        result.append("activeState", state.activeState);
        return result;
    };
    
    checkErrors = (): Promise<boolean> => {
        const state = this.state;
        return new Promise<boolean>((resolve) => {
            if (!state.code) {
                store.customStore.dispatch(storeActions.showErrorMessage(Translations.NameMustBeDefined));
                return resolve(false);
            }
            return resolve(true);
        });
    };

    saveToDb = async (): Promise<ITaskStateListItem> => {
        const props = this.props;
        const state = this.state;
        if (!await AppUtils.validate(this.checkErrors)) return null;
        const cacheDataToTaskTypeDialog = this.isNewTaskType();
        let newId = state.id;
        let newRowId = state.rowId;
        if (!cacheDataToTaskTypeDialog) {
            //Save to server
            const saveData = this.getSaveDataFromState(props, state);
            const result = await AppUtils.callService(() => taskService.saveTaskStateEdit(saveData.obj));
            if (!result) return null;
            //Set new id for return data
            newId = result.id;
            newRowId = result.rowId;
        } else {
            //Save to server when new task type is saved, generate new id for new item to avoid conflict with other items in the list before server save
            if (state.isNew) {
                newId = Base.getGuid();
            }
        }
        //Update state and hash
        const stateChange = { id: newId, rowId: newRowId };
        this.orgStateHash = this.getSaveDataFromState(props, { ...state, ...stateChange }).hash;
        this.setState(stateChange);
        //Return new list item
        return new TaskStateListItem({
            id: newId,
            companyId: props.companyId,
            taskTypeId: props.taskTypeId,
            code: state.code,
            color: state.color,
            comment: state.comment,
            sortOrder: state.sortOrder.toInteger(),
            editabilityType: state.editabilityType,
            activeState: state.activeState
        });
    };

    handleSave = async (closeDialog: boolean) => {
        const cacheDataToTaskTypeDialog = this.isNewTaskType();
        const result = await this.saveToDb();
        if (!result) return;
        if (!closeDialog || cacheDataToTaskTypeDialog) {
            this.props.onSaved(result);
        }
        if (!closeDialog) {
            this.loadData(result.id);
            return;
        }
        this.props.onClose(true);
    };

    handleRemove = async () => {
        const props = this.props;
        const state = this.state;
        if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToRemoveTaskStateParameter, state.item.code), false) === ConfirmationDialogResult.Yes) {
            // Call server
            const result = await props.onRemove([state.id]);
            if (!result) return;
            props.onClose(true);
        }
    };

    handleCancel = async () => {
        const props = this.props;
        const state = this.state;
        const cancelResult = await AppUtils.cancel(this.getSaveDataFromState(props, state).hash, this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            this.handleSave(true);
        } else {
            props.onClose(false);
        }
    };

    canMoveOut = async (): Promise<boolean> => {
        const props = this.props;
        const cacheDataToTaskTypeDialog = this.isNewTaskType();
        return await AppUtils.canMoveOut(this.getSaveDataFromState(props, this.state).hash, this.orgStateHash,
            async () => {
                const result = await this.saveToDb();
                if (!result) return false;
                if (cacheDataToTaskTypeDialog) {
                    this.props.onSaved(result);
                }
                return true;
            },
            props.onRefreshList,
            (newStateHash) => { this.orgStateHash = newStateHash; }
        );
    };

    handlePrevious = async () => {
        if (!await this.canMoveOut()) return;
        this.props.onPrevious();
    };

    handleNext = async () => {
        if (!await this.canMoveOut()) return;
        this.props.onNext();
    };

    render() {
        const props = this.props;
        const state = this.state;
        if (!state.item) return null;
        const isNew = state.isNew;
        return (
            <Dialog
                id="taskStateDialog"
                maxWidth="sm"
                title={isNew ? Translations.AddTaskState : (Translations.TaskState + ": " + state.item.code)}
                body={
                    <Box display="flex" flexDirection="column">
                        <GridRow>
                            <Grid item sm={12}>
                                <FormField title={Translations.Name} fullWidth required margin="0">
                                    <TextField name="code" title={Translations.Name} inputProps={{ maxLength: 50 }} value={state.code} required={true} onChange={this.handleChange} size="small" fullWidth disabled={props.isReadOnly} />
                                </FormField>
                            </Grid>
                        </GridRow>
                        <GridRow>
                            <Grid item sm={12}>
                                <FormField title={Translations.Description} fullWidth>
                                    <TextField name="comment" title={Translations.Description} inputProps={{ maxLength: 1000 }} value={state.comment} onChange={this.handleChange} multiline rows={5} size="small" fullWidth disabled={props.isReadOnly} />
                                </FormField>
                            </Grid>
                        </GridRow>
                        <GridRow>
                            <Grid item sm={4}>
                                <FormField title={Translations.Color} fullWidth overflow="visible">
                                    <ColorPicker
                                        requiredColors={[state.color]}
                                        value={state.color}
                                        disabled={props.isReadOnly}
                                        onChange={color => this.setState({ color })}
                                    />
                                </FormField>
                            </Grid>
                            <Grid item sm={4}>
                                <FormField title={Translations.SortOrder} fullWidth>
                                    <TextField name="sortOrder" title={Translations.SortOrder} type="number" inputProps={{ maxLength: 5 }} value={state.sortOrder} onChange={this.handleChange} size="small" fullWidth InputProps={{ inputProps: { min: 1, max: 100 } }} disabled={props.isReadOnly} />
                                </FormField>
                            </Grid>
                            <Grid item sm={4}>
                                <FormField title={<span>&nbsp;</span>} fullWidth>
                                    <FormCheckBox
                                        title={Translations.Locked}
                                        checked={state.editabilityType > TaskStateEditabilityType.NoLimitation}
                                        disabled={props.isReadOnly}
                                        onChange={(checked: boolean) => { this.setState({ editabilityType: checked ? TaskStateEditabilityType.ReadOnly : TaskStateEditabilityType.NoLimitation }); }}
                                    />
                                </FormField>
                            </Grid>
                        </GridRow>
                        <GridRow>
                            <Grid item sm={12}>
                                <FormField title="" fullWidth>
                                    <FormCheckBox
                                        title={Translations.InUse}
                                        checked={state.activeState > 0}
                                        disabled={props.isReadOnly}
                                        onChange={(checked: boolean) => { this.setState({ activeState: checked ? 1 : 0 }); }}
                                    />
                                </FormField>
                            </Grid>
                        </GridRow>
                    </Box>
                }
                actions={
                    <Box display="flex" justifyContent="space-between" width="100%">
                        <DialogItemsNav
                            modifyUser={!isNew ? state.item.modifyUser : null}
                            hasPrevious={props.hasPrevious}
                            hasNext={props.hasNext}
                            onPrevious={this.handlePrevious}
                            onNext={this.handleNext}
                        />
                        <DialogButtons
                            isNew={isNew}
                            removeDisabled={props.isReadOnly}
                            saveDisabled={props.isReadOnly}
                            onCancel={this.handleCancel}
                            onRemove={!isNew ? this.handleRemove : null}
                            onSave={() => this.handleSave(false)}
                        />
                    </Box>
                }
                onClose={this.handleCancel}
            />
        );
    }
}